import { Button } from '@/components/ui/button';
import { JSONContent } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import BulletList from '@tiptap/extension-bullet-list';
import { ListItem } from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import { EditorContent, mergeAttributes, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold as BoldIcon,
  Heading1,
  Heading2,
  Heading3,
  Italic as ItalicIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Undo2
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import './TipTap.css';

const ToolbarButton = ({
  onClick,
  isActive,
  children,
  disabled = false,
}: {
  onClick: () => void;
  isActive?: boolean;
  children: ReactNode;
  disabled?: boolean;
}) => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-md border border-input p-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 select-none';

  const inactiveClasses = 'bg-transparent hover:bg-accent hover:text-accent-foreground';

  const activeClasses =
    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground';

  return (
    <button
      type="button"
      draggable="false"
      onContextMenu={(e) => e.preventDefault()}
      disabled={disabled}
      className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {children}
    </button>
  );
};

interface RichTextEditorProps {
  /**
   * Initial content to load in the editor. Can be provided either as
   * a TipTap JSON document or an HTML string.
   */
  initialContent?: JSONContent | null;

  /**
   * Callback invoked every time the editor updates. Returns the current
   * document in TipTap/ProseMirror JSON format so it can be persisted
   * (e.g. in a database or API).
   */
  onContentChange?: (content: JSONContent) => void;

  /**
   * Callback triggered when the user clicks the "Save" button.
   * Receives the current document as TipTap JSON.
   */
  onSave?: (content: JSONContent) => void;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  onContentChange,
  onSave,
}) => {
  const {t} = useTranslation();

  // Normalize the initial content to JSON so that we can compare documents.
  const parseToJson = (input: JSONContent | string): JSONContent => {
    if (typeof input === 'string') {
      // TipTap can parse HTML into JSON, but the core API does not expose that directly.
      // As a fallback, if we receive HTML we return an empty document; the editor
      // will still hydrate with the `content` prop later.
      return { type: 'doc', content: [] } as JSONContent;
    }
    return input;
  };

  const [content, setContent] = useState<JSONContent | string>(
    initialContent ?? '<p>Probando texto...</p>'
  );

  const initialJsonRef = useRef<JSONContent>(
    parseToJson(initialContent ?? { type: 'doc', content: [] })
  );

  const [isDirty, setIsDirty] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ListItem,
      BulletList,
      OrderedList,
      Blockquote,
    ],
    content,
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
        spellcheck: 'false',
      },
    },
    onUpdate({ editor }) {
      // We serialise the current state to JSON so it can be compared.
      const json = editor.getJSON();
      setContent(json);

      // Check whether the document has changed compared with the initial content.
      const changed = JSON.stringify(json) !== JSON.stringify(initialJsonRef.current);
      setIsDirty(changed);

      onContentChange?.(json);

      // Debug helper
      console.debug('Editor content updated (JSON)', json);
    },
  });

  if (!editor) return null;

  return (
    <div className="w-full relative prose prose-sm max-w-none tiptapViewer">
      {/* Toolbar */}
      <div className="top-0 z-20 flex flex-wrap gap-1 p-2 border border-input rounded-t-md bg-muted/70 backdrop-blur">
        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleBold().run();
          }}
          isActive={editor.isActive('bold')}
        >
          <BoldIcon size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleItalic().run();
          }}
          isActive={editor.isActive('italic')}
        >
          <ItalicIcon size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleStrike().run();
          }}
          isActive={editor.isActive('strike')}
        >
          <Strikethrough size={16} />
        </ToolbarButton>

        <div className="w-px h-8 bg-border mx-1" />

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleHeading({ level: 1 }).run();
          }}
          isActive={editor.isActive('heading', { level: 1 })}
        >
          <Heading1 size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleHeading({ level: 2 }).run();
          }}
          isActive={editor.isActive('heading', { level: 2 })}
        >
          <Heading2 size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
          isActive={editor.isActive('heading', { level: 3 })}
        >
          <Heading3 size={16} />
        </ToolbarButton>

        <div className="w-px h-8 bg-border mx-1" />

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleBulletList().run();
          }}
          isActive={editor.isActive('bulletList')}
        >
          <List size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleOrderedList().run();
          }}
          isActive={editor.isActive('orderedList')}
        >
          <ListOrdered size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleBlockquote().run();
          }}
          isActive={editor.isActive('blockquote')}
        >
          <Quote size={16} />
        </ToolbarButton>

        <div className="w-px h-8 bg-border mx-1" />

        {/* <ToolbarButton
          onClick={() => {
            editor.chain().focus().toggleCodeBlock().run();
          }}
          isActive={editor.isActive('codeBlock')}
        >
          <Code2 size={16} />
        </ToolbarButton> */}

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().setHorizontalRule().run();
          }}
        >
          <Minus size={16} />
        </ToolbarButton>

        <div className="w-px h-8 bg-border mx-1" />

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().undo().run();
          }}
          disabled={!editor.can().undo()}
        >
          <Undo2 size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            editor.chain().focus().redo().run();
          }}
          disabled={!editor.can().redo()}
        >
          <Redo2 size={16} />
        </ToolbarButton>
      </div>

      {/* Editor area */}
      <div className="border border-t-0 border-input rounded-b-md px-3 max-h-[75vh] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      {/* Save button */}
      <div className="mt-2 flex justify-end">
        <Button
          disabled={!isDirty}
          onClick={() => {
            if (!editor) return;
            const json = editor.getJSON();

            onSave?.(json);
            // After saving, mark the document as clean.
            initialJsonRef.current = json;
            setIsDirty(false);
          }}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
};  
